/**
 * Wrapper de la pestaña "Ranking" que separa visualmente los dos rankings:
 *  - Ranking de actividades (puntajes durante el evento) — vista principal,
 *    es la que más importa al director mientras el evento está en curso.
 *  - Ranking de inscripción (orden en que los clubes completaron registro y
 *    primer pago) — vista secundaria.
 *
 * Para los directores el ranking se filtra a su propio club; para admins
 * se muestra el ranking global.
 */

import { useMemo, useState } from "react";
import {
  Trophy,
  ClipboardList,
  Sparkles,
  Activity as ActivityIcon,
} from "lucide-react";
import type { Event, EventRegistration } from "@/types";
import { useAuthStore } from "@/stores/authStore";
import {
  useEventActivities,
  useEventActivityCategories,
  useEventActivityScores,
} from "@/hooks/useEventActivities";
import EventRankingPanel from "./EventRankingPanel";
import { EventActivityRankingPanel } from "./EventActivityRankingPanel";

type SubTab = "activities" | "registration";

interface Props {
  event: Event;
  registrations: EventRegistration[];
  isAdmin: boolean;
}

export function EventRankingTab({ event, registrations, isAdmin }: Props) {
  const [tab, setTab] = useState<SubTab>("activities");
  const profile = useAuthStore((s) => s.profile);
  const directorClub = profile?.directorOfClub;

  const { data: categories } = useEventActivityCategories(event.id);
  const { data: activities } = useEventActivities(event.id);
  const { data: scores } = useEventActivityScores(event.id);

  // Clubes inscritos derivados de registrations (id + name únicos)
  const clubs = useMemo(() => {
    const map = new Map<string, string>();
    registrations.forEach((r) => {
      if (r.clubId && r.clubName) map.set(r.clubId, r.clubName);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [registrations]);

  const totalScored = scores?.length ?? 0;

  return (
    <div className="space-y-4">
      {/* Encabezado con tabs visualmente atractivos */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 p-5 text-white shadow-lg animate-fade-in-down">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full bg-amber-300/20 blur-2xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center animate-float">
            <Trophy className="w-7 h-7 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold leading-tight">
              Ranking del evento
            </h2>
            <p className="text-sm text-white/80">
              {isAdmin
                ? "Visualiza la clasificación de todos los clubes."
                : "Tu posición y desempeño durante el evento."}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-xs font-medium bg-white/15 backdrop-blur rounded-full px-3 py-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-200" />
            <span>{totalScored} puntaje(s) registrado(s)</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="relative mt-5 inline-flex p-1 rounded-xl bg-white/10 backdrop-blur">
          <SubTabButton
            active={tab === "activities"}
            onClick={() => setTab("activities")}
            icon={<ActivityIcon className="w-4 h-4" />}
            label="Actividades"
            hint="Lo que importa hoy"
          />
          <SubTabButton
            active={tab === "registration"}
            onClick={() => setTab("registration")}
            icon={<ClipboardList className="w-4 h-4" />}
            label="Inscripción"
            hint="Orden de cierre"
          />
        </div>
      </div>

      {/* Contenido animado al cambiar de pestaña */}
      <div key={tab} className="animate-fade-in-up">
        {tab === "activities" ? (
          <EventActivityRankingPanel
            eventId={event.id}
            eventName={event.name}
            scores={scores ?? []}
            clubs={clubs}
            categories={categories ?? []}
            activities={activities ?? []}
            isAdmin={isAdmin}
            restrictToClubId={!isAdmin ? directorClub?.id : undefined}
          />
        ) : (
          <EventRankingPanel event={event} />
        )}
      </div>
    </div>
  );
}

interface SubTabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
}

function SubTabButton({
  active,
  onClick,
  icon,
  label,
  hint,
}: SubTabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 " +
        (active
          ? "bg-white text-purple-700 shadow-md scale-105"
          : "text-white/85 hover:text-white hover:bg-white/10")
      }
    >
      <span
        className={
          "flex items-center justify-center transition-transform duration-300 " +
          (active ? "scale-110" : "")
        }
      >
        {icon}
      </span>
      <div className="text-left">
        <p className="leading-tight">{label}</p>
        <p
          className={
            "text-[10px] font-normal leading-none mt-0.5 " +
            (active ? "text-purple-500" : "text-white/60")
          }
        >
          {hint}
        </p>
      </div>
      {active && (
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-amber-400 animate-glow" />
      )}
    </button>
  );
}

export default EventRankingTab;
